import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Vapi from '@vapi-ai/web';

/*
 * CONFIGURATION:
 * 
 * 1. ASSISTANT_ID: Replace 'YOUR_ASSISTANT_ID_HERE' with your actual Vapi Assistant ID
 *    - Get this from: Vapi Dashboard → Assistants → Copy the ID
 *    - The assistant should have the goToSlide function configured
 * 
 * 2. N8N ENDPOINTS:
 *    - https://auto.trackingfaster.com/webhook/createconversation
 *      Receives: { conversation_id, timestamp, status }
 *      Purpose: Create a new conversation record when call starts
 * 
 *    - https://auto.trackingfaster.com/webhook/getslidepage
 *      Receives: conversation_id as query parameter
 *      Returns: { page: number } (1-based page numbers)
 *      Purpose: Poll for slide changes during the call
 *      Defaults to page 1 if no response received
 * 
 * 3. The AI assistant will call n8n webhooks to change slides
 *    - Configure this in the Vapi dashboard
 *    - The assistant will send the conversation_id when changing slides
 */

// Call status enum for better state management
const CALL_STATUS = {
  INACTIVE: 'inactive',
  ACTIVE: 'active',
  LOADING: 'loading'
};

const SalesPitchPage = () => {
  const location = useLocation();
  const [showSpecialButtons, setShowSpecialButtons] = useState(false);
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [callStatus, setCallStatus] = useState(CALL_STATUS.INACTIVE);
  const [isSpeechActive, setIsSpeechActive] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [conversationId, setConversationId] = useState(null); // Add conversation ID state
  // const [messages, setMessages] = useState([]); // Unused for now, but useful for future transcript display
  
  // Get selected agents and user_id from navigation state with localStorage fallbacks
  const selectedAgents = useMemo(() => {
    const fromState = location.state?.selectedAgents;
    if (Array.isArray(fromState) && fromState.length) return fromState;
    try {
      const stored = JSON.parse(localStorage.getItem('selected_agents') || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (_) {
      return [];
    }
  }, [location.state]);

  const userId = useMemo(() => {
    const fromState = location.state?.userId;
    if (fromState) return fromState;
    try {
      const stored = localStorage.getItem('user_id');
      return stored || null;
    } catch (_) {
      return null;
    }
  }, [location.state]);

  // Resolve userId from multiple sources (state, URL, localStorage)
  const getEffectiveUserId = useCallback(() => {
    const stateId = location.state?.userId;
    if (stateId) return stateId;
    try {
      const params = new URLSearchParams(window.location.search);
      const urlId = params.get('user_id') || params.get('userid') || params.get('userId') || params.get('client_reference_id');
      if (urlId) return urlId;
    } catch (_) {}
    try {
      const stored = localStorage.getItem('user_id');
      if (stored) return stored;
    } catch (_) {}
    return null;
  }, [location.state]);

  // Persist effective userId if found
  useEffect(() => {
    const uid = getEffectiveUserId();
    if (uid) {
      try { localStorage.setItem('user_id', uid); } catch (_) {}
    }
  }, [getEffectiveUserId]);

  // Precompute Stripe URL once so countdown or re-renders don't drop query param
  const STRIPE_BASE_URL = 'https://buy.stripe.com/6oUdR8ePI0j004Z2mlcQU0e';
  const stripeCheckoutUrl = useMemo(() => {
    const uid = getEffectiveUserId();
    return uid ? `${STRIPE_BASE_URL}?client_reference_id=${encodeURIComponent(uid)}` : STRIPE_BASE_URL;
  }, [getEffectiveUserId]);
  
  

      // Vapi configuration - Create single instance like the example (memoized to prevent recreation)
    const vapi = useMemo(() => {
      const instance = new Vapi('03b4b1dd-c71d-4bb0-addb-a0e9a490182a');
      return instance;
    }, []);
  
  // Assistant ID from Vapi dashboard
  const ASSISTANT_ID = '0a59795c-546c-4b00-9990-ba985948d385';
  
  // Slide configuration
  const TOTAL_SLIDES = 21; // You have 21 slides
  const SLIDE_DISCOVERY_MAX = TOTAL_SLIDES; // Allow some buffer for future slides
  
  // Debug: Log configuration (only once)
    useEffect(() => {
      // Vapi Config loaded
    }, []);

  // Initialize Vapi event handlers (following example pattern)
  useEffect(() => {
    const onCallStart = () => {
      setCallStatus(CALL_STATUS.ACTIVE);
        
        if (conversationId) {
          // Try to inject conversation ID again if we have it but no controlUrl was found earlier
          // This is a fallback in case the controlUrl becomes available after the call starts
          if (conversationId && !window.conversationIdInjected) {
            // We'll need to get the controlUrl from somewhere - maybe from the vapi instance
            // For now, just note that we need this
          }
        }
    };

    const onCallEnd = () => {
      setCallStatus(CALL_STATUS.INACTIVE);
      setIsSpeechActive(false);
    };

    const onSpeechStart = () => {
      setIsSpeechActive(true);
    };

    const onSpeechEnd = () => {
      setIsSpeechActive(false);
    };

    const onVolumeLevel = (volume) => {
      setAudioLevel(volume);
    };

    const onError = (error) => {
        console.error('Vapi error:', error);
      setCallStatus(CALL_STATUS.INACTIVE);
    };

    const onMessage = (message) => {
        // Handle different message types
      if (message.type === 'function-call') {
          // Function call message
        } else if (message.type === 'transcript') {
          // If this is the first assistant message, we can try to inject context here
          if (message.role === 'assistant' && conversationId && !window.conversationIdInjected) {
            // The AI now knows about the conversation ID through our n8n integration
            window.conversationIdInjected = true;
          }
        } else if (message.type === 'conversation-update') {
          // Conversation update
        } else {
          // Other message type
        }
    };

    // Function call handler for slide control
    const onFunctionCall = (data) => {
      const { name, args } = data;
      
      if (name === 'goToSlide' && typeof window.setSlide === 'function') {
        const slideIndex = Number(args?.index ?? 0);
        window.setSlide(slideIndex);
      } else if (name === 'getSlideContent' && typeof window.getSlideContent === 'function') {
        const slideNumber = Number(args?.slideNumber ?? 1);
        window.getSlideContent(slideNumber);
      }
    };

    // Register all event listeners (following example pattern)
    vapi.on('call-start', onCallStart);
    vapi.on('call-end', onCallEnd);
    vapi.on('speech-start', onSpeechStart);
    vapi.on('speech-end', onSpeechEnd);
    vapi.on('volume-level', onVolumeLevel);
    vapi.on('error', onError);
    vapi.on('message', onMessage);
    vapi.on('function-call', onFunctionCall);

    // Cleanup function (following example pattern)
    return () => {
      vapi.off('call-start', onCallStart);
      vapi.off('call-end', onCallEnd);
      vapi.off('speech-start', onSpeechStart);
      vapi.off('speech-end', onSpeechEnd);
      vapi.off('volume-level', onVolumeLevel);
      vapi.off('error', onError);
      vapi.off('message', onMessage);
      vapi.off('function-call', onFunctionCall);
    };
  }, [conversationId, vapi]);



  // Send conversation ID to n8n to create conversation record
  const createConversationRecord = async (conversationId, controlUrl = null) => {
    try {
      const payload = { 
        conversation_id: conversationId,
        timestamp: new Date().toISOString(),
        status: 'active',
        selected_agents: selectedAgents,
        user_id: userId
      };
      
      // Include control URL if available
      if (controlUrl) {
        payload.control_url = controlUrl;
      }
      
      
      const response = await fetch('https://auto.trackingfaster.com/webhook/createconversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        console.error('Failed to create conversation record in n8n');
      }
    } catch (error) {
      console.error('Error creating conversation record:', error);
    }
  };

  // Inject conversation ID into AI conversation history using WebSocket SDK (avoid CORS)
  const injectConversationIdViaWebSocket = useCallback(async (conversationId) => {
    try {
      // Try using Vapi SDK WebSocket methods first
      if (vapi && typeof vapi.sendControlMessage === 'function') {
        try {
          await vapi.sendControlMessage({
            type: 'add-message',
            message: {
              role: 'system',
              content: `Here is the conversation ID: ${conversationId}. Use this ID when making any requests to external systems or when referencing this specific conversation.`
            },
            triggerResponseEnabled: false
          });
          return 'websocket';
        } catch (sdkError) {
          // Vapi SDK sendControlMessage method failed
        }
      }
      
      // Try using Vapi SDK addMessage method
      if (vapi && typeof vapi.addMessage === 'function') {
        try {
          await vapi.addMessage({
            role: 'system',
            content: `Here is the conversation ID: ${conversationId}. Use this ID when making any requests to external systems or when referencing this specific conversation.`
          });
          return 'websocket';
        } catch (sdkError) {
          // Vapi SDK addMessage method failed
        }
      }
      
      // Try using Vapi SDK sendMessage method
      if (vapi && typeof vapi.sendMessage === 'function') {
        try {
          await vapi.sendMessage({
            role: 'system',
            content: `Here is the conversation ID: ${conversationId}. Use this ID when making any requests to external systems or when referencing this specific conversation.`
          });
          return 'websocket';
        } catch (sdkError) {
          // Vapi SDK sendMessage method failed
        }
      }
      
      // Try using the message event to inject context
      if (vapi && typeof vapi.emit === 'function') {
        try {
          vapi.emit('add-message', {
            role: 'system',
            content: `Here is the conversation ID: ${conversationId}. Use this ID when making any requests to external systems or when referencing this specific conversation.`
          });
          return 'websocket';
        } catch (sdkError) {
          // Vapi SDK emit method failed
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error in WebSocket SDK injection:', error);
      return false;
    }
  }, [vapi]);

  // Inject conversation ID into AI conversation history using controlUrl
  const injectConversationIdToAI = useCallback(async (conversationId, controlUrl) => {
    try {
      // Validate control URL format
      if (!controlUrl || typeof controlUrl !== 'string') {
        throw new Error('Invalid control URL format');
      }
      
      // Try using Vapi SDK method first (if available)
      if (vapi && typeof vapi.addMessage === 'function') {
        try {
          await vapi.addMessage({
            role: 'system',
            content: `Here is the conversation ID: ${conversationId}. Use this ID when making any requests to external systems or when referencing this specific conversation.`
          });
          return 'controlurl';
        } catch (sdkError) {
          // Vapi SDK method failed, trying HTTP fallback
        }
      }
      
      // Try other potential SDK methods
      if (vapi && typeof vapi.sendMessage === 'function') {
        try {
          await vapi.sendMessage({
            role: 'system',
            content: `Here is the conversation ID: ${conversationId}. Use this ID when making any requests to external systems or when referencing this specific conversation.`
          });
          return 'controlurl';
        } catch (sdkError) {
          // Vapi SDK sendMessage method failed, trying HTTP fallback
        }
      }
      
      // Try using the message event to inject context
      if (vapi && typeof vapi.emit === 'function') {
        try {
          vapi.emit('add-message', {
            role: 'system',
            content: `Here is the conversation ID: ${conversationId}. Use this ID when making any requests to external systems or when referencing this specific conversation.`
          });
          return 'controlurl';
        } catch (sdkError) {
          // Vapi SDK emit method failed, trying HTTP fallback
        }
      }
      
      // Fallback to HTTP method
      
      // Ensure the URL is properly formatted
      let url = controlUrl;
      if (controlUrl.startsWith('<') && controlUrl.endsWith('>')) {
        // Remove angle brackets if present (as shown in the API docs)
        url = controlUrl.slice(1, -1);
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'add-message',
          message: {
            role: 'system',
            content: `Here is the conversation ID: ${conversationId}. Use this ID when making any requests to external systems or when referencing this specific conversation.`
          },
          triggerResponseEnabled: false // Don't trigger AI response, just add context
        })
      });
      
      if (response.ok) {
        return 'controlurl';
      } else {
        console.error('Failed to inject conversation ID into AI:', response.status);
        return false;
      }
    } catch (error) {
      console.error('Error injecting conversation ID into AI:', error);
      return false;
    }
  }, [vapi]);

  // Enhanced Vapi control functions
  const startCall = async () => {
    try {
      setCallStatus(CALL_STATUS.LOADING);
      
      if (ASSISTANT_ID === 'YOUR_ASSISTANT_ID_HERE') {
        throw new Error('Please configure your Assistant ID in the code. Replace "YOUR_ASSISTANT_ID_HERE" with your actual Vapi Assistant ID.');
      }
      
      // Try to start the call
      const result = await vapi.start(ASSISTANT_ID);
      
      // Check if we need to wait for the call-start event
      if (result && result.id) {
        setConversationId(result.id); // Capture conversation ID
         
         // Extract controlUrl from the result if available
         const controlUrl = result.monitor?.controlUrl;
         
         // Create record for the new call (including control URL if available)
         createConversationRecord(result.id, controlUrl);
        
        // Try WebSocket SDK method first (avoid CORS)
        if (controlUrl) {
          // Try WebSocket SDK methods to avoid CORS
          const wsInjectionSuccess = await injectConversationIdViaWebSocket(result.id);
          if (wsInjectionSuccess) {
            window.conversationIdInjected = wsInjectionSuccess;
          } else {
            // Fallback to control URL method
            const injectionSuccess = await injectConversationIdToAI(result.id, controlUrl);
            window.conversationIdInjected = injectionSuccess;
          }
        } else {
          window.conversationIdInjected = false;
        }
        
        // Don't set status to ACTIVE yet - wait for call-start event
      } else {
        console.warn('No call ID received from vapi.start()');
      }
    } catch (error) {
      console.error('Failed to start call:', error);
      setCallStatus(CALL_STATUS.INACTIVE);
      
      if (error.message.includes('Assistant ID')) {
        alert('Configuration Error: ' + error.message);
      } else if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
        alert(`CORS Error: The Vapi API is blocking requests from your domain. This is a server-side configuration issue that needs to be resolved by Vapi support. Please contact them to whitelist your domain: ${window.location.origin}`);
      } else {
        alert(`Connection failed: ${error.message}. Please check your internet connection and try again.`);
      }
    }
  };

  const stopCall = async () => {
    try {
      setCallStatus(CALL_STATUS.LOADING);
      await vapi.stop();
    } catch (error) {
      console.error('Failed to stop call:', error);
      setCallStatus(CALL_STATUS.ACTIVE); // Revert if stop fails
    }
  };

  const toggleCall = () => {
    if (callStatus === CALL_STATUS.ACTIVE) {
      stopCall();
    } else {
      startCall();
    }
  };

  // Frontend-only slide discovery: probe sequentially until a gap of N misses.
  useEffect(() => {
    let cancelled = false;
    const discovered = [];
    const MAX = SLIDE_DISCOVERY_MAX; // Use the configured maximum
    let consecutiveMisses = 0;
    const MAX_MISSES = 2; // Stop after 2 consecutive misses

    function tryLoad(i) {
      if (i > MAX || consecutiveMisses >= MAX_MISSES || cancelled) {
        setSlides(discovered);
        setLoading(false);
        return;
      }
      const filename = `/slides_png/slide${i}.png`;
      const img = new Image();
      img.onload = () => {
        discovered.push(filename);
        consecutiveMisses = 0;
        tryLoad(i + 1);
      };
      img.onerror = () => {
        consecutiveMisses += 1;
        tryLoad(i + 1);
      };
      img.src = filename;
    }
    tryLoad(1);
    return () => { cancelled = true; };
  }, [SLIDE_DISCOVERY_MAX]);

  // Hash-based control (#slide=3)
  useEffect(() => {
    function applyHash() {
      const m = window.location.hash.match(/slide=(\d+)/);
      if (m) {
        const idx = parseInt(m[1], 10) - 1;
        setCurrentIndex(idx >= 0 ? idx : 0);
      }
    }
    window.addEventListener('hashchange', applyHash);
    applyHash();
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  // Expose global setter for automation/bookmarklet
  useEffect(() => {
    // Get slide content from n8n webhook for AI context
    const getSlideContent = async (slideNumber) => {
      if (!conversationId) {
        return null;
      }
      
      try {
        const response = await fetch(`https://auto.trackingfaster.com/webhook/getslide?page_no=${slideNumber}&conversation_id=${conversationId}`);
        if (response.ok) {
          const data = await response.json();
          return data;
        }
      } catch (error) {
        console.error('Failed to fetch slide content:', error);
      }
      return null;
    };

    window.setSlide = (n) => {
      if (typeof n !== 'number') return;
      
      if (!slides.length) return;
      
      setCurrentIndex(prev => {
        const clamped = Math.min(Math.max(0, Math.floor(n)), slides.length - 1);
        
        if (clamped !== prev) {
          window.location.hash = `slide=${clamped + 1}`;
        }
        return clamped;
      });
    };
    
    // Expose getSlideContent globally for AI agent
    window.getSlideContent = getSlideContent;
    
    return () => { 
      delete window.setSlide; 
      delete window.getSlideContent;
    };
  }, [slides, currentIndex, conversationId]);

  // Force start at slide 1 if none specified (ensures hash and state set early)
  useEffect(() => {
    if (!loading && slides.length && !/slide=\d+/.test(window.location.hash)) {
      window.location.hash = 'slide=1';
      if (typeof window.setSlide === 'function') window.setSlide(0);
    }
  }, [loading, slides]);

  // Set timer for special buttons (4 minutes after first load)
  useEffect(() => {
    if (!loading && slides.length) {
      const timer = setTimeout(() => setShowSpecialButtons(true), 240000); // 4 minutes
      return () => clearTimeout(timer);
    }
  }, [loading, slides]);

  // Poll n8n for slide changes using conversation ID (replaces old PHP polling)
  useEffect(() => {
    if (!conversationId || !slides.length) return;
    

    
    let cancelled = false;
    let lastApplied = -1;
    
    async function pollN8n() {
      try {
        const response = await fetch(`https://auto.trackingfaster.com/webhook/getslidepage?conversation_id=${conversationId}&ts=${Date.now()}`, { 
          cache: 'no-store' 
        });
        
        if (response.ok) {
          try {
            const json = await response.json();

            
            // Handle different response formats
            let slideIndex = 0; // Default to first slide
            
            if (json && json.success === "true" && Number.isFinite(json.index)) {
              // Format: {"success":"true","index":4}
              slideIndex = json.index - 1; // Convert to 0-based index

            } else if (json && Number.isFinite(json.page)) {
              // Format: {"page": 4} (fallback)
              slideIndex = json.page - 1; // Convert page number to slide index (0-based)

            } else {
              // No valid response, default to slide 1
              slideIndex = 0;

            }
            
            if (slideIndex !== lastApplied) {

              window.setSlide(slideIndex);
              lastApplied = slideIndex;

            } else {

            }
          } catch (jsonError) {
            console.warn('n8n response not valid JSON:', jsonError);
            // If JSON parsing fails, default to page 1
            if (lastApplied !== 0) {
              window.setSlide(0);
              lastApplied = 0;

            }
          }
        } else {
          // If no response received, default to page 1 (slide index 0)
          if (lastApplied !== 0) {
            window.setSlide(0);
            lastApplied = 0;
            
          }
        }
      } catch(e) {
        console.warn('n8n polling error:', e);
        // On error, also default to page 1
        if (lastApplied !== 0) {
          window.setSlide(0);
          lastApplied = 0;
          
        }
      } finally {
        if (!cancelled) setTimeout(pollN8n, 2000);
      }
    }
    
    pollN8n();
    return () => { cancelled = true; };
  }, [conversationId, slides.length, SLIDE_DISCOVERY_MAX]);

  const navigate = useNavigate();

  const goBack = () => {
    navigate(-1);
  };

  const goToBooking = () => {
    const uid = getEffectiveUserId();
    navigate('/book-call', { state: { fromPitch: true, userId: uid } });
  };

  return (
    <div className="sales-pitch-page" style={{minHeight: '100vh', backgroundColor: '#eef2f5'}}>
      {/* Main Content - Full Height Split Layout */}
      <div className="main-content" style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'stretch'
      }}>
        
        {/* Left Side - Slide Deck */}
        <div className="slide-deck" style={{
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          borderRight: '1px solid #e5e7eb'
        }}>
          
          {/* Slide Display */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
            justifyContent: 'flex-start'
          }}>
            <div style={{
              flex: 1, 
              position: 'relative', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              background: '#000', 
              borderRadius: '8px', 
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 200px)',
              minHeight: '400px'
            }}>
              {!loading && slides.length > 0 && (
                <div className="slide-label">
                  {`Slide Page ${currentIndex + 1}`}
                </div>
              )}
              {loading && (
                <span style={{color:'#fff', fontSize:'0.9rem'}}>Loading...</span>
              )}
              {!loading && slides.length > 0 && (
                <img
                  key={currentIndex}
                  className="slide-image"
                  src={slides[currentIndex]}
                  alt={"Slide " + (currentIndex+1)}
                  style={{
                    maxWidth: '90%',
                    maxHeight: '90%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    transition: 'opacity 0.4s ease',
                    display: 'block'
                  }}
                />
              )}
            </div>
          </div>

          {/* Status Footer */}
          <div style={{height:'8px', borderTop: '1px solid #e5e7eb', backgroundColor:'#eef2f5'}} />
        </div>

        {/* Right Side - Controls and Buttons */}
        <div className="controls-panel" style={{
          width: '400px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#eef2f5',
          padding: '24px'
        }}>

          {/* Special Buttons - Always show but greyed out initially */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              textAlign: 'center'
            }}>
            <h3 style={{marginBottom: '16px', color: '#16a34a'}}>
                Special Offers Available
              </h3>
            
            {/* Activation Message */}
            <p style={{
              fontSize: '0.9rem',
              color: '#6b7280',
              marginBottom: '20px',
              fontStyle: 'italic'
            }}>
              * The Free Agents Will be Automatically Activated if You Stay on This Call Till The End
            </p>
            
              <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                <button 
                onClick={() => {
                  if (showSpecialButtons) {
                    window.open(stripeCheckoutUrl, '_blank');
                  }
                }}
                  className="btn btn-large mega-deal-pulse"
                disabled={!showSpecialButtons}
                  style={{
                  background: showSpecialButtons 
                    ? 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)' 
                    : '#e5e7eb',
                  color: showSpecialButtons ? 'white' : '#9ca3af',
                    border: 'none',
                    fontWeight: 'bold',
                    width: '100%',
                  padding: '16px',
                  cursor: showSpecialButtons ? 'pointer' : 'not-allowed',
                  opacity: showSpecialButtons ? 1 : 0.6
                  }}
                >
                  🔥 Activate MEGA DEAL
                </button>
                <button 
                onClick={() => {
                  if (showSpecialButtons) {
                    window.open('https://www.upwork.com/freelancers/rahuldanj?mp_source=share', '_blank');
                  }
                }}
                  className="btn btn-secondary"
                disabled={!showSpecialButtons}
                  style={{
                    width: '100%',
                  padding: '12px',
                  cursor: showSpecialButtons ? 'pointer' : 'not-allowed',
                  opacity: showSpecialButtons ? 1 : 0.6,
                  backgroundColor: showSpecialButtons ? '#6b7280' : '#e5e7eb',
                  color: showSpecialButtons ? 'white' : '#9ca3af'
                  }}
                >
                  ⭐ View Our Customer Reviews
                </button>
              </div>
            </div>

          {/* Enhanced AI Agent Box with Vapi SDK */}
          <div className="vapi-agent-box" style={{
            background: 'white',
            borderRadius: '16px',
            padding: '0',
            marginBottom: '20px',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            height: '220px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{padding: '14px 18px', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                             <h3 style={{margin:0, fontSize:'1rem', color: '#16a34a'}}>Agent</h3>
              <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                <span style={{fontSize:'0.65rem', letterSpacing:'0.05em', color:'#6b7280'}}>
                  {callStatus === CALL_STATUS.ACTIVE ? 'LIVE' : callStatus === CALL_STATUS.LOADING ? 'CONNECTING...' : 'READY'}
                </span>
                {/* Audio level indicator */}
                {callStatus === CALL_STATUS.ACTIVE && (
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: isSpeechActive ? '#ff4444' : '#12A594',
                    animation: isSpeechActive ? 'pulse 1s infinite' : 'none'
                  }}></div>
                )}
              </div>
            </div>
            
            <div style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'space-between', padding:'16px'}}>
              {/* Status Display - Enhanced */}
              <div style={{textAlign:'center', marginBottom:'12px'}}>
                <p style={{fontSize:'0.9rem', color:'#6b7280', margin:0, fontWeight:'500'}}>
                  {callStatus === CALL_STATUS.ACTIVE ? '🎙️ Call in progress' : callStatus === CALL_STATUS.LOADING ? '⏳ Connecting...' : '🎯 Ready to start call'}
                </p>
                {callStatus === CALL_STATUS.ACTIVE && (
                  <p style={{fontSize:'0.7rem', color:'#059669', margin:'4px 0 0 0'}}>
                    {isSpeechActive ? 'Assistant Speaking...' : 'Listening...'}
                  </p>
                )}

                                 
              </div>
              
              {/* Control Button - Enhanced with audio visualization */}
              <div style={{display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', flex:1, gap:'8px'}}>
                <button
                  onClick={toggleCall}
                  disabled={callStatus === CALL_STATUS.LOADING}
                  style={{
                    background: callStatus === CALL_STATUS.ACTIVE ? '#dc2626' : '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px 28px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: callStatus === CALL_STATUS.LOADING ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: callStatus === CALL_STATUS.LOADING ? 0.6 : 1,
                    minWidth: '140px',
                    boxShadow: callStatus === CALL_STATUS.ACTIVE ? `0 0 ${10 + audioLevel * 20}px ${audioLevel * 5}px rgba(220, 38, 38, 0.3)` : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (callStatus !== CALL_STATUS.LOADING) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    if (callStatus === CALL_STATUS.ACTIVE) {
                      e.target.style.boxShadow = `0 0 ${10 + audioLevel * 20}px ${audioLevel * 5}px rgba(220, 38, 38, 0.3)`;
                    } else {
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {callStatus === CALL_STATUS.ACTIVE ? 'End Call' : callStatus === CALL_STATUS.LOADING ? 'Connecting...' : 'Start Call'}
                </button>
                
                {/* Small "Book a call with human" link */}
                <button 
                  onClick={goToBooking}
                  style={{
                    fontSize: '0.7rem',
                    color: '#6b7280',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    textAlign: 'center',
                    background: 'none',
                    border: 'none',
                    padding: 0
                  }}
                >
                  Book a call with a human instead
                </button>
              </div>
              
              {/* Connection Info - Enhanced */}
              {callStatus === CALL_STATUS.ACTIVE && (
                <div style={{textAlign:'center', marginTop:'8px'}}>
                  <p style={{fontSize:'0.75rem', color:'#059669', margin:0}}>
                    ✓ Connected to Agent
                  </p>
                  <p style={{fontSize:'0.7rem', color:'#6b7280', margin:'2px 0 0 0'}}>
                    Speak naturally to control slides
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Spacer */}
          <div style={{flex: 1}}></div>

          {/* Back Button */}
          <div style={{marginTop: '20px'}}>
            <button onClick={goBack} className="btn btn-secondary" style={{width: '100%'}}>
              ← Back
            </button>
          </div>

        </div>
      </div>
      
      {/* Audio level visualization styles */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        .slide-label {
          position: absolute;
          top: 10px;
          left: 10px;
          background: rgba(0, 0, 0, 0.6);
          color: #fff;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 0.9rem;
          z-index: 2;
        }
        
        @media (max-width: 900px) {
          .main-content {
            flex-direction: column !important;
          }
          
          .slide-deck {
            order: 1 !important;
            flex: none !important;
            border-right: none !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
          
          .controls-panel {
            order: 2 !important;
            width: 100% !important;
            overflow-y: visible !important;
          }
          
          .slide-image {
            max-width: 95% !important;
            max-height: 90% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SalesPitchPage;
